// maths.portll.net — parent-facing "Game progress" card. Reads the child's in-app mastery plane
// (getMastery) and summarises it warmly: explored / confident / practising, with a per-strand line
// (grouped by skill-id prefix, so no engine dependency). Progress is framed as exploration — never a
// score or grade (project tone). Renders nothing until data resolves; a friendly empty state if unplayed.
import { useEffect, useState } from "react";
import { Card } from "./ui.jsx";
import { getMastery } from "../lib/mastery.js";

const STRAND = (id) =>
  id.startsWith("num.") || id.startsWith("frac.") ? "Number"
  : id.startsWith("alg.") ? "Algebra"
  : id.startsWith("meas.") ? "Measurement"
  : id.startsWith("stat.") ? "Statistics"
  : id.startsWith("prob.") ? "Probability"
  : id.startsWith("space.") ? "Space"
  : "Other";
const CONFIDENT = (s) => s === "attained" || s === "fluent";

export default function GameProgressCard({ uid, childId, name }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    getMastery(uid, childId).then((m) => { if (live) setData(m); }).catch(() => { if (live) setData({}); });
    return () => { live = false; };
  }, [uid, childId]);

  if (!data) return null;
  const ids = Object.keys(data);
  const who = (name || "They").split(" ")[0];

  if (!ids.length) {
    return (
      <Card>
        <h2 style={{ margin: "0 0 .4rem" }}>Game progress</h2>
        <p style={{ margin: 0, color: "var(--ink-soft)" }}>
          Nothing from the maths game yet. When {who} plays, what they’ve explored shows up here — never as a score.
        </p>
      </Card>
    );
  }

  const confident = ids.filter((id) => CONFIDENT(data[id].state)).length;
  const byStrand = {};
  ids.forEach((id) => {
    const s = STRAND(id);
    byStrand[s] = byStrand[s] || { total: 0, confident: 0 };
    byStrand[s].total++;
    if (CONFIDENT(data[id].state)) byStrand[s].confident++;
  });

  return (
    <Card>
      <h2 style={{ margin: "0 0 .4rem" }}>Game progress</h2>
      <p style={{ margin: "0 0 .8rem", color: "var(--ink-soft)" }}>
        {ids.length} skill{ids.length === 1 ? "" : "s"} explored · {confident} confident · {ids.length - confident} still practising. Something to explore, never a grade.
      </p>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: ".35rem" }}>
        {Object.keys(byStrand).sort().map((s) => (
          <li key={s} style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
            <span>{s}</span>
            <span style={{ color: "var(--ink-soft)" }}>{byStrand[s].confident}/{byStrand[s].total} confident</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
